/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2026 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2026 - Finnish Meteorological Institute (FMI)
 * Copyright 2026 - The Norwegian Meteorological Institute (MET Norway)
 * */

import type { Feature, FeatureCollection } from 'geojson';
import type { layerTypes } from '@opengeoweb/store';

interface SimplifiedFeatureLayer {
  id: string;
  originalId?: string;
  geoJSON: FeatureCollection;
}

// Normalizes feature/layer ids from unknown values into string keys used in maps/sets.
const getFeatureKey = (value: unknown): string | undefined =>
  value !== undefined && value !== null ? String(value) : undefined;

const getGeoFeatureSelectionKey = (
  feature: GeoJSON.Feature,
): string | undefined =>
  getFeatureKey(feature.id) || getFeatureKey(feature.properties?.originalId);

// Extracts warning-dialog feature layers into a minimal shape used by map sync helpers.
// Used to sync changes in warning-dialog map to map views
export const createWarningDialogFeatureLayers = (
  warningDialogFeatureLayers: layerTypes.ReduxLayer[],
): SimplifiedFeatureLayer[] =>
  warningDialogFeatureLayers
    .filter(
      (
        layer,
      ): layer is layerTypes.ReduxLayer & {
        id: string;
        geojson: FeatureCollection;
      } => !!layer.id && !!layer.geojson,
    )
    .map((layer) => ({
      id: layer.id,
      originalId: layer.originalId,
      geoJSON: layer.geojson,
    }));

// Builds a lookup set of all ids/originalIds present in warning-dialog layers.
// Used to prevent rendering duplicate features from linked panel layers.
export const createWarningDialogFeatureKeys = (
  warningDialogFeatures: SimplifiedFeatureLayer[],
): Set<string> => {
  const keys = new Set<string>();
  warningDialogFeatures.forEach((featureLayer) => {
    featureLayer.geoJSON.features.forEach((feature) => {
      const featureIdKey = getFeatureKey(feature.id);
      const originalIdKey = getFeatureKey(feature.properties?.originalId);
      if (featureIdKey) {
        keys.add(featureIdKey);
      }
      if (originalIdKey) {
        keys.add(originalIdKey);
      }
    });
    const layerOriginalIdKey = getFeatureKey(featureLayer.originalId);
    if (layerOriginalIdKey) {
      keys.add(layerOriginalIdKey);
    }
  });
  return keys;
};

// Creates a reverse index: feature key -> panel ids that own that feature.
// Used to route hover/selection updates to the correct linked panel(s).
export const getPanelIdsByFeatureKey = (
  linkedFeatureLayersByPanel: Record<string, layerTypes.ReduxLayer[]>,
): Record<string, string[]> => {
  const ownerById = new Map<string, Set<string>>();

  const addOwner = (key: string | undefined, panelId: string): void => {
    if (!key) {
      return;
    }
    const owners = ownerById.get(key) ?? new Set<string>();
    owners.add(panelId);
    ownerById.set(key, owners);
  };

  Object.entries(linkedFeatureLayersByPanel).forEach(([panelId, layers]) => {
    layers.forEach((layer) => {
      addOwner(getFeatureKey(layer.originalId), panelId);
      layer.geojson?.features.forEach((feature) => {
        addOwner(getFeatureKey(feature.id), panelId);
        addOwner(getFeatureKey(feature.properties?.originalId), panelId);
      });
    });
  });

  const ownersByKey: Record<string, string[]> = {};
  ownerById.forEach((owners, key) => {
    ownersByKey[key] = Array.from(owners);
  });
  return ownersByKey;
};

export const createSelectedFeatureIdsByLayerKey = (
  featureLayers: layerTypes.ReduxLayer[],
): Map<string, Set<string>> => {
  const selectedFeatureIdsByLayerKey = new Map<string, Set<string>>();

  featureLayers.forEach((layer) => {
    const featureLayerId = layer.featureSync?.featureLayerKey;
    if (!featureLayerId || !layer.selectedFeatureIds?.length) {
      return;
    }

    selectedFeatureIdsByLayerKey.set(
      featureLayerId,
      new Set(layer.selectedFeatureIds),
    );
  });

  return selectedFeatureIdsByLayerKey;
};

// Map selections must only update panels directly linked to the clicked map.
// Linked maps can form a transitive graph, but using the full graph here would
// update panels that were explicitly unlinked from the clicked map.
export const getFeatureSelectionTargetPanelIds = (
  selectedFeatureId: string,
  featureOwnerPanelIdsById: Record<string, string[]>,
  directlyLinkedPanelIds: string[],
): string[] => {
  const directlyLinkedPanelIdSet = new Set(directlyLinkedPanelIds);
  const ownerPanelIds = selectedFeatureId
    ? (featureOwnerPanelIdsById[selectedFeatureId] ?? [])
    : [];

  if (ownerPanelIds.length > 0) {
    return ownerPanelIds.filter((panelId) =>
      directlyLinkedPanelIdSet.has(panelId),
    );
  }

  if (directlyLinkedPanelIds.length === 1) {
    return [directlyLinkedPanelIds[0]];
  }

  return [];
};

// Merges linked panel feature layers into one collection for map rendering,
// while filtering out features that are already represented by warning-dialog layers.
export const createLinkedFeatureCollection = (
  linkedFeatureLayers: layerTypes.ReduxLayer[],
  warningDialogFeatureKeys: Set<string>,
  selectedFeatureIdsByLayerKey: Map<string, Set<string>> = new Map(),
): FeatureCollection => {
  const features: Feature[] = linkedFeatureLayers.flatMap((featureLayer) =>
    (featureLayer.geojson?.features ?? [])
      .filter((geoFeature): boolean => {
        const featureId = getFeatureKey(geoFeature.id);
        const originalId =
          getFeatureKey(geoFeature.properties?.originalId) ||
          getFeatureKey(featureLayer.originalId);
        return !(
          (featureId && warningDialogFeatureKeys.has(featureId)) ||
          (originalId && warningDialogFeatureKeys.has(originalId))
        );
      })
      .map((geoFeature): Feature => {
        const featureLayerKey =
          featureLayer.featureSync?.featureLayerKey || featureLayer.id;
        const selectedFeatureIds = featureLayerKey
          ? selectedFeatureIdsByLayerKey.get(featureLayerKey)
          : undefined;
        const isSelected = selectedFeatureIds?.has(
          getGeoFeatureSelectionKey(geoFeature) ?? '',
        );

        const existingProperties =
          geoFeature.properties && typeof geoFeature.properties === 'object'
            ? (geoFeature.properties as Record<string, unknown>)
            : {};

        return {
          ...geoFeature,
          properties: {
            ...existingProperties,
            ...(isSelected ? { highlightSelectedFeature: true } : undefined),
            ...(featureLayer.originalId
              ? { originalId: featureLayer.originalId }
              : undefined),
          },
        };
      }),
  );

  return {
    type: 'FeatureCollection',
    features,
  };
};
